import { useCallback, useEffect, useState } from 'react'

/**
 * Reusable "saved prompts" — short named snippets the user can drop into
 * the chat or context fields without retyping. Stored in localStorage so
 * the slice is private to the device (we don't have a backend table for
 * prompts yet, and pinning them client-side keeps PII out of the audit
 * log until that table arrives).
 *
 * Schema is intentionally small + forwards-compatible: each prompt is a
 * {id, name, body, updatedAt} record. Future server-backed prompts will
 * map onto the same shape so the picker UI does not need to change.
 */

const STORAGE_KEY = 'docfiller.saved-prompts.v1'
const STORAGE_EVENT = 'docfiller:saved-prompts'

export const MAX_PROMPT_BODY_CHARS = 4_000
export const MAX_PROMPTS = 50

export interface SavedPrompt {
  id: string
  name: string
  body: string
  updatedAt: string
}

interface SavedPromptInput {
  name: string
  body: string
}

function safeRead(): SavedPrompt[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(isSavedPrompt)
  } catch {
    return []
  }
}

function isSavedPrompt(value: unknown): value is SavedPrompt {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return (
    typeof v.id === 'string' &&
    typeof v.name === 'string' &&
    typeof v.body === 'string' &&
    typeof v.updatedAt === 'string'
  )
}

function safeWrite(prompts: SavedPrompt[]): void {
  if (typeof window === 'undefined') return
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(prompts))
    window.dispatchEvent(new CustomEvent(STORAGE_EVENT))
  } catch {
    // Quota exceeded / disabled storage — silently ignore. The UI degrades
    // to "no prompts" but never throws.
  }
}

function nextId(existing: SavedPrompt[]): string {
  // No crypto in the test harness; a millisecond-suffixed counter is plenty
  // for human-driven CRUD where collisions just mean a duplicate id during
  // the same millisecond, and existence is checked before write.
  const stamp = Date.now().toString(36)
  let i = 0
  let id = `p_${stamp}_${i}`
  while (existing.some((p) => p.id === id)) {
    i += 1
    id = `p_${stamp}_${i}`
  }
  return id
}

export interface SavedPromptsApi {
  prompts: SavedPrompt[]
  isAtCapacity: boolean
  /**
   * Returns the saved prompt — caller uses the id to drive selection state.
   * Throws if the body is empty or the slot count would exceed
   * ``MAX_PROMPTS`` so the UI can render the error without first writing
   * an invalid value into storage.
   */
  save: (input: SavedPromptInput) => SavedPrompt
  update: (id: string, input: SavedPromptInput) => SavedPrompt
  remove: (id: string) => void
  clearAll: () => void
}

/**
 * Hook wrapper around localStorage so React components subscribe to the
 * same source of truth and re-render together (the custom event fires
 * inside the tab; the native ``storage`` event covers other tabs).
 */
export function useSavedPrompts(): SavedPromptsApi {
  const [prompts, setPrompts] = useState<SavedPrompt[]>(() => safeRead())

  useEffect(() => {
    const onChange = () => setPrompts(safeRead())
    window.addEventListener('storage', onChange)
    window.addEventListener(STORAGE_EVENT, onChange as EventListener)
    return () => {
      window.removeEventListener('storage', onChange)
      window.removeEventListener(STORAGE_EVENT, onChange as EventListener)
    }
  }, [])

  const save = useCallback<SavedPromptsApi['save']>((input) => {
    const name = input.name.trim()
    const body = input.body.trim()
    if (!name) throw new Error('Name cannot be empty')
    if (!body) throw new Error('Body cannot be empty')
    if (body.length > MAX_PROMPT_BODY_CHARS) {
      throw new Error(
        `Body too long — trim to ${MAX_PROMPT_BODY_CHARS.toLocaleString()} characters`,
      )
    }
    const current = safeRead()
    if (current.length >= MAX_PROMPTS) {
      throw new Error(
        `Saved prompt limit reached (${MAX_PROMPTS}). Delete one to add a new one.`,
      )
    }
    const id = nextId(current)
    const prompt: SavedPrompt = {
      id,
      name,
      body,
      updatedAt: new Date().toISOString(),
    }
    const next = [prompt, ...current]
    safeWrite(next)
    setPrompts(next)
    return prompt
  }, [])

  const update = useCallback<SavedPromptsApi['update']>((id, input) => {
    const name = input.name.trim()
    const body = input.body.trim()
    if (!name) throw new Error('Name cannot be empty')
    if (!body) throw new Error('Body cannot be empty')
    if (body.length > MAX_PROMPT_BODY_CHARS) {
      throw new Error(
        `Body too long — trim to ${MAX_PROMPT_BODY_CHARS.toLocaleString()} characters`,
      )
    }
    const current = safeRead()
    const idx = current.findIndex((p) => p.id === id)
    if (idx === -1) throw new Error('Prompt not found')
    const updated: SavedPrompt = {
      id,
      name,
      body,
      updatedAt: new Date().toISOString(),
    }
    const next = [...current]
    next[idx] = updated
    safeWrite(next)
    setPrompts(next)
    return updated
  }, [])

  const remove = useCallback<SavedPromptsApi['remove']>((id) => {
    const current = safeRead()
    const next = current.filter((p) => p.id !== id)
    safeWrite(next)
    setPrompts(next)
  }, [])

  const clearAll = useCallback<SavedPromptsApi['clearAll']>(() => {
    safeWrite([])
    setPrompts([])
  }, [])

  return {
    prompts,
    isAtCapacity: prompts.length >= MAX_PROMPTS,
    save,
    update,
    remove,
    clearAll,
  }
}

/** Test helper — reset the underlying storage between cases. */
export function __resetSavedPromptsForTests(): void {
  if (typeof window === 'undefined') return
  window.localStorage.removeItem(STORAGE_KEY)
  window.dispatchEvent(new CustomEvent(STORAGE_EVENT))
}
